import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { LeadReminder } from '../types';

/**
 * One reminder row with inline edit. Used by LeadDetail and ClientDetail.
 *
 * The parent owns the data + network calls AND the translated labels —
 * this component just gates local edit state and renders the chrome.
 * Labels are passed in so the same component slots into either the
 * `leads` or `clients` translation namespace without bundling both.
 *
 * Reminders that have already pushed (pushSentAt set) lock to read-
 * only since the server refuses PATCH on those (409 already_fired).
 */
export type ReminderLabels = {
  edit: string;
  delete: string;
  save: string;
  editCancel: string;
  notePlaceholder: string;
  pendingPush: string;
};

export function ReminderRow({
  reminder,
  labels,
  onSave,
  onDelete,
}: {
  reminder: LeadReminder;
  labels: ReminderLabels;
  onSave: (next: { remindAt: string; note: string | null }) => Promise<void>;
  onDelete: () => Promise<void>;
}) {
  const { i18n } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [draftRemindAt, setDraftRemindAt] = useState(() =>
    toDateTimeLocal(reminder.remindAt),
  );
  const [draftNote, setDraftNote] = useState(reminder.note ?? '');

  const fired = reminder.pushSentAt !== null;

  function startEdit() {
    setDraftRemindAt(toDateTimeLocal(reminder.remindAt));
    setDraftNote(reminder.note ?? '');
    setEditing(true);
  }

  async function save() {
    if (!draftRemindAt || busy) return;
    setBusy(true);
    try {
      await onSave({
        remindAt: new Date(draftRemindAt).toISOString(),
        note: draftNote.trim() || null,
      });
      setEditing(false);
    } finally {
      setBusy(false);
    }
  }

  async function del() {
    if (busy) return;
    setBusy(true);
    try {
      await onDelete();
    } finally {
      setBusy(false);
    }
  }

  if (editing) {
    return (
      <div className="reminder-row">
        <div className="reminder-edit-grid">
          <div className="field">
            <input
              type="datetime-local"
              value={draftRemindAt}
              onChange={(e) => setDraftRemindAt(e.target.value)}
            />
          </div>
          <div className="field">
            <input
              value={draftNote}
              onChange={(e) => setDraftNote(e.target.value)}
              placeholder={labels.notePlaceholder}
              maxLength={500}
            />
          </div>
        </div>
        <button className="btn-primary" onClick={() => void save()} disabled={busy || !draftRemindAt}>
          {labels.save}
        </button>
        <button className="btn-cancel" onClick={() => setEditing(false)} disabled={busy}>
          {labels.editCancel}
        </button>
      </div>
    );
  }

  return (
    <div className="case-row" style={{ alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600 }}>
          {new Date(reminder.remindAt).toLocaleString(i18n.language, {
            weekday: 'short',
            day: '2-digit',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </div>
        {reminder.note && (
          <div style={{ fontSize: 13, color: 'var(--text2)' }}>{reminder.note}</div>
        )}
        {!fired && (
          <div style={{ fontSize: 11, color: 'var(--text3)' }}>{labels.pendingPush}</div>
        )}
      </div>
      {!fired && (
        <button className="btn-cancel" onClick={startEdit} disabled={busy}>
          {labels.edit}
        </button>
      )}
      <button className="btn-cancel" onClick={() => void del()} disabled={busy}>
        {labels.delete}
      </button>
    </div>
  );
}

function toDateTimeLocal(iso: string): string {
  // datetime-local wants YYYY-MM-DDTHH:mm in the local zone (no offset).
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
